import {
  Button,
  Input,
  Select,
  Table,
  TableColumnsType,
} from 'antd';
import moment from 'moment';
import { ReactNode, useEffect, useMemo, useState } from 'react';
import {
  FaArrowDown,
  FaArrowUp,
  FaBusinessTime,
  FaChartArea,
  FaChartBar,
  FaChartLine,
  FaChartPie,
  FaDollarSign,
} from 'react-icons/fa';
import { FiRefreshCcw, FiSearch } from 'react-icons/fi';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { DetailTitreEntity } from '../../../../entities/detailTitre.entity';
import { DetenteurEntity } from '../../../../entities/detenteur.entity';
import { PRIMARY, YELLOW } from '../../../../shared/colors';
import { formatNum } from '../../../../shared/constants';
import { ExportFormatButton } from '../../../../shared/ExportFormatButton';
import { getExportMetadata } from '../../../../shared/exportFiles';
import {
  getUserPortefeuille,
  getUserPortefeuilleKPI,
} from '../../../dashboard/components/Monitor/network';
import { Layout } from '../../../shared/Layout';
import { exportPortefeuilleData } from '../components/exportDataExcel';

const Container = styled.div`
  min-height: calc(100vh - 76px);
  background:
    radial-gradient(circle at 8% 0%, rgba(255, 79, 31, 0.08), transparent 24%),
    radial-gradient(circle at 90% 8%, rgba(255, 176, 0, 0.08), transparent 20%),
    var(--mt-bg);
  color: var(--mt-text);
  font-family: Poppins-R;

  .bread-crum span {
    color: var(--mt-text);
  }

  td.ant-table-column-sort {
    background-color: transparent !important;
  }

  th {
    background-color: ${PRIMARY} !important;
    color: #fff7e8 !important;
    font-weight: bold;
  }

  .odd-row {
    background-color: var(--mt-row-odd);
    color: var(--mt-text);
  }

  .even-row {
    background-color: var(--mt-row-even);
    color: var(--mt-text);
  }

  .odd-row,
  .even-row {
    &:hover > td {
      background-color: var(--mt-row-hover) !important;
      cursor: pointer;
      color: var(--mt-text);
    }
  }

  .content {
    padding: 12px;
    width: 100%;
    color: var(--mt-text);
  }

  .pnl-hero {
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    gap: 16px;
    border: 1px solid var(--mt-border);
    border-radius: 8px;
    padding: 18px;
    background:
      linear-gradient(135deg, rgba(255, 79, 31, 0.13), transparent 38%),
      ${PRIMARY};
    box-shadow: var(--mt-card-shadow);
  }

  .eyebrow {
    color: ${YELLOW};
    font-family: Poppins-B;
    font-size: 11px;
    letter-spacing: 0.08em;
    text-transform: uppercase;
  }

  .pnl-hero h1 {
    margin: 5px 0 0;
    color: #fff7e8;
    font-family: Poppins-B;
    font-size: 30px;
    line-height: 1.1;
  }

  .pnl-hero p {
    max-width: 820px;
    margin: 8px 0 0;
    color: rgba(255, 247, 232, 0.72);
    line-height: 1.5;
  }

  .hero-actions,
  .page-toolbar {
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
  }

  .ghost-action,
  .primary-action {
    border-radius: 999px;
    font-family: Poppins-B;
  }

  .ghost-action {
    border-color: rgba(255, 176, 0, 0.28);
    background: rgba(255, 255, 255, 0.06);
    color: #fff7e8;
  }

  .primary-action {
    border: 0;
    background: linear-gradient(135deg, #ff4f1f, ${YELLOW});
    color: #07141c;
  }

  .kpi-grid {
    display: grid;
    grid-template-columns: repeat(5, minmax(0, 1fr));
    gap: 10px;
    margin-top: 12px;
  }

  .kpi-card,
  .toolbar,
  .table-shell {
    border: 1px solid var(--mt-border);
    border-radius: 8px;
    background: var(--mt-card);
    box-shadow: var(--mt-card-shadow);
  }

  .kpi-card {
    display: flex;
    align-items: center;
    justify-content: space-between;
    min-height: 84px;
    padding: 13px;
  }

  .kpi-card .title {
    color: var(--mt-muted);
    font-size: 11px;
    letter-spacing: 0.05em;
    text-transform: uppercase;
  }

  .kpi-card .value {
    display: block;
    margin-top: 6px;
    color: var(--mt-text);
    font-family: Poppins-B;
    font-size: 19px;
  }

  .kpi-card .icon {
    display: grid;
    place-items: center;
    width: 36px;
    height: 36px;
    border: 1px solid rgba(255, 176, 0, 0.24);
    border-radius: 8px;
    color: var(--mt-warning);
    background: var(--mt-surface);
  }

  .toolbar {
    display: grid;
    grid-template-columns: minmax(280px, 460px) 1fr;
    align-items: center;
    gap: 12px;
    margin-top: 12px;
    padding: 12px;
  }

  .search-box {
    position: relative;
  }

  .search-box svg {
    position: absolute;
    z-index: 1;
    top: 50%;
    left: 12px;
    color: var(--mt-muted);
    transform: translateY(-50%);
  }

  .search-box .ant-input {
    height: 38px;
    border-color: var(--mt-border);
    border-radius: 999px;
    padding-left: 36px;
    background: var(--mt-surface);
    color: var(--mt-text);
  }

  .filter-grid {
    display: grid;
    grid-template-columns: repeat(3, minmax(130px, 1fr));
    gap: 8px;
    margin-top: 10px;
  }

  .filter-grid .ant-select-selector {
    min-height: 38px;
    border-color: var(--mt-border) !important;
    border-radius: 8px !important;
    background: var(--mt-surface) !important;
    color: var(--mt-text) !important;
  }

  .filter-grid .ant-select-selection-item,
  .filter-grid .ant-select-selection-placeholder {
    color: var(--mt-text) !important;
  }

  .table-shell {
    margin-top: 12px;
    overflow: hidden;
  }

  .table-title {
    display: flex;
    align-items: center;
    justify-content: space-between;
    min-height: 46px;
    padding: 10px 14px;
    border-bottom: 1px solid var(--mt-border);
    background: ${PRIMARY};
    color: ${YELLOW};
    font-family: Poppins-B;
    text-transform: uppercase;
  }

  .table-count {
    border: 1px solid rgba(255, 176, 0, 0.25);
    border-radius: 999px;
    padding: 5px 9px;
    color: #fff7e8;
    font-size: 11px;
    text-transform: none;
  }

  .pnl-table .ant-table {
    background: var(--mt-card);
    color: var(--mt-text);
  }

  .pnl-table .ant-table-thead > tr > th {
    border-bottom: 1px solid var(--mt-border);
    font-size: 11px;
    text-transform: uppercase;
  }

  .pnl-table .ant-table-tbody > tr > td {
    border-bottom: 1px solid var(--mt-border);
    color: var(--mt-text);
  }

  .holder-cell,
  .position-cell {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  .holder-cell strong,
  .position-cell strong {
    color: var(--mt-text);
    font-family: Poppins-B;
  }

  .holder-cell span,
  .position-cell span {
    color: var(--mt-muted);
    font-size: 11px;
  }

  .amount-cell {
    display: block;
    text-align: right;
    font-family: Poppins-B;
    white-space: nowrap;
  }

  .pnl-positive {
    color: #00965d;
  }

  .pnl-negative {
    color: #dc2626;
  }

  .positions-panel {
    margin: 8px 4px 12px;
    overflow: hidden;
    border: 1px solid var(--mt-border);
    border-radius: 10px;
    background: var(--mt-card);
  }

  .positions-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 10px 12px;
    border-bottom: 1px solid var(--mt-border);
    background: var(--mt-surface);
  }

  .positions-head strong {
    display: block;
    color: var(--mt-text);
    font-family: Poppins-B;
  }

  .positions-head span {
    color: var(--mt-muted);
    font-size: 11px;
  }

  .nested-pnl-table {
    margin: 0;
  }

  @media (max-width: 760px) {
    .content {
      padding: 18px 12px;
    }

    .pnl-hero,
    .toolbar {
      align-items: stretch;
      display: flex;
      flex-direction: column;
    }

    .kpi-grid {
      grid-template-columns: 1fr;
    }

    .filter-grid {
      grid-template-columns: 1fr;
    }
  }
`;

export const ReportingPortefeuillePage = () => {
  const [portefeuilleKPI, setPortefeuilleKPI] = useState<any>();
  const [portefeuille, setPortefeuille] = useState<DetenteurEntity[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [pnlFilter, setPnlFilter] = useState('all');
  const [agencyFilter, setAgencyFilter] = useState('all');
  const [instrumentFilter, setInstrumentFilter] = useState('all');
  const [isLoading, setIsLoading] = useState(true);
  const connectedUser = useSelector((state: any) => state.userReducer).user;

  useEffect(() => {
    fetchData().finally(() => setIsLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchData = async () => {
    setIsLoading(true);

    await getUserPortefeuille(connectedUser.id).then(({ data }) => {
      setPortefeuille(data);
    });

    await getUserPortefeuilleKPI(connectedUser.id).then(({ data }) => {
      setPortefeuilleKPI(data);
    });
    setIsLoading(false);
  };

  const filteredPortefeuille = useMemo(() => {
    const term = searchTerm.toLowerCase().trim();

    return portefeuille.filter((pf: any) => {
      const searchable = [
        pf.code_referent,
        pf.nom,
        pf.prenom,
        pf.adresse,
        pf.email,
        pf.mobile,
        pf.agence?.nom,
        pf.agence?.code,
        pf.createur?.nom,
        pf.createur?.prenom,
        pf.created_at,
        ...(pf.detailTitres || []).flatMap((position: DetailTitreEntity) => [
          position.titre?.pays,
          position.titre?.code,
          position.titre?.instrument,
          position.parentCode,
        ]),
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      const matchesSearch = !term || searchable.includes(term);
      const matchesPnl =
        pnlFilter === 'all' ||
        (pnlFilter === 'positive' && Number(pf.pnl || 0) >= 0) ||
        (pnlFilter === 'negative' && Number(pf.pnl || 0) < 0);
      const matchesAgency =
        agencyFilter === 'all' || pf.agence?.code === agencyFilter;
      const matchesInstrument =
        instrumentFilter === 'all' ||
        (pf.detailTitres || []).some(
          (position: DetailTitreEntity) =>
            position.titre?.instrument === instrumentFilter,
        );

      return matchesSearch && matchesPnl && matchesAgency && matchesInstrument;
    });
  }, [agencyFilter, instrumentFilter, pnlFilter, portefeuille, searchTerm]);

  const filterOptions = useMemo(
    () => ({
      agencies: uniqueOptions(portefeuille.map((pf: any) => pf.agence?.code)),
      instruments: uniqueOptions(
        portefeuille.flatMap((pf: any) =>
          (pf.detailTitres || []).map(
            (position: DetailTitreEntity) => position.titre?.instrument,
          ),
        ),
      ),
    }),
    [portefeuille],
  );

  const localStats = useMemo(
    () => ({
      amount: filteredPortefeuille.reduce(
        (sum: number, pf: any) => sum + Number(pf.montantTotal || 0),
        0,
      ),
      pnl: filteredPortefeuille.reduce(
        (sum: number, pf: any) => sum + Number(pf.pnl || 0),
        0,
      ),
      quantity: filteredPortefeuille.reduce(
        (sum: number, pf: any) => sum + Number(pf.qteTotal || 0),
        0,
      ),
      holders: filteredPortefeuille.length,
    }),
    [filteredPortefeuille],
  );

  return (
    <Layout>
      <Container>
        {/** Obtain 'current' values in Sidebar component */}
        <div className='content'>
          <section className='pnl-hero'>
            <div>
              <span className='eyebrow'>Portefeuille des titres</span>
              <h1>Dashboard PNL</h1>
              <p>
                Suivez la performance consolidée, la valeur de marché, le rendement
                et les positions par détenteur avec une lecture orientée trading desk.
              </p>
            </div>
            <div className='hero-actions'>
              <Button className='ghost-action' icon={<FiRefreshCcw />} onClick={fetchData}>
                Rafraîchir
              </Button>
              <ExportFormatButton
                className='primary-action'
                onExport={(format) => exportPortefeuilleData(filteredPortefeuille, format, getExportMetadata(connectedUser))}
              />
            </div>
          </section>

          <section className='kpi-grid'>
            <KPIContainer
              title='PNL'
              value={`${formatNum(portefeuilleKPI?.pnl ?? localStats.pnl) || '--'} XAF`}
              icon={<FaChartLine />}
            />
            <KPIContainer
              title='Valeur du portefeuille'
              value={
                `${formatNum(portefeuilleKPI?.valeurPortefeuille ?? localStats.amount) || '--'} XAF`
              }
              icon={<FaDollarSign />}
            />
            <KPIContainer
              title='Rendement'
              value={
                `${formatNum(roundMetric(portefeuilleKPI?.rendement)) || '--'} %`
              }
              icon={<FaChartBar />}
            />
            <KPIContainer
              title='Valeur du marché'
              value={
                `${formatNum(roundMetric(portefeuilleKPI?.valeurMarche)) || '--'} %`
              }
              icon={<FaChartArea />}
            />
            <KPIContainer
              title='Duration'
              value={
                `${formatNum(roundMetric(portefeuilleKPI?.duration)) || '--'} ans`
              }
              icon={<FaBusinessTime />}
            />
          </section>

          <section className='toolbar'>
            <div>
              <div className='search-box'>
                <FiSearch />
                <Input
                  placeholder='Rechercher détenteur, titre, agence, pays...'
                  value={searchTerm}
                  onChange={(event) => setSearchTerm(event.target.value)}
                />
              </div>
              <div className='filter-grid'>
                <Select value={pnlFilter} onChange={setPnlFilter}>
                  <Select.Option value='all'>Tous P&L</Select.Option>
                  <Select.Option value='positive'>P&L positif</Select.Option>
                  <Select.Option value='negative'>P&L négatif</Select.Option>
                </Select>
                <Select value={agencyFilter} onChange={setAgencyFilter}>
                  <Select.Option value='all'>Toutes agences</Select.Option>
                  {filterOptions.agencies.map((agency) => (
                    <Select.Option key={agency} value={agency}>
                      {agency}
                    </Select.Option>
                  ))}
                </Select>
                <Select value={instrumentFilter} onChange={setInstrumentFilter}>
                  <Select.Option value='all'>Tous instruments</Select.Option>
                  {filterOptions.instruments.map((instrument) => (
                    <Select.Option key={instrument} value={instrument}>
                      {instrument}
                    </Select.Option>
                  ))}
                </Select>
              </div>
            </div>
            <div className='page-toolbar'>
              <span className='table-count'>
                {filteredPortefeuille.length} détenteurs · Qté {formatNum(localStats.quantity) || '--'}
              </span>
            </div>
          </section>

          <section className='table-shell'>
            <div className='table-title'>
              <span>
                <FaChartPie style={{ marginRight: 7, marginBottom: -2 }} />
                Mon portefeuille
              </span>
              <span className='table-count'>{portefeuille.length} détenteurs total</span>
            </div>
            <Table
              loading={isLoading}
              columns={COLUMNS}
              dataSource={filteredPortefeuille}
              rowClassName={(row, index) =>
                index % 2 === 0 ? 'even-row' : 'odd-row'
              }
              className='pnl-table'
              expandable={{
                expandedRowRender: (row: any) => {
                  return (
                    <div className='positions-panel'>
                      <div className='positions-head'>
                        <div>
                          <strong>Positions de {row.nom} {row.prenom || ''}</strong>
                          <span>{row.detailTitres?.length || 0} titres en portefeuille</span>
                        </div>
                        <span>PNL {formatNum(row.pnl) || '--'} XAF</span>
                      </div>
                      <Table<DetailTitreEntity>
                        className='nested-pnl-table pnl-table'
                        columns={SUB_COLUMNS}
                        dataSource={(row as any)?.detailTitres || []}
                        rowKey='id'
                        rowClassName={(row, index) =>
                          index % 2 === 0 ? 'even-row' : 'odd-row'
                        }
                        size='small'
                        scroll={{ x: 1180 }}
                        pagination={false}
                      />
                    </div>
                  );
                },
              }}
              rowKey='id'
              size='small'
              scroll={{ x: 1180 }}
              pagination={false}
            />
          </section>
        </div>
      </Container>
    </Layout>
  );
};

const KPIContainer = ({
  title,
  value,
  icon,
}: {
  title: string;
  value: string;
  icon: ReactNode;
}) => {
  return (
    <div className='kpi-card'>
      <div>
        <div className='title'>{title}</div>
        <div className='value'>{value}</div>
      </div>
      <div className='icon'>{icon}</div>
    </div>
  );
};

const roundMetric = (value?: number) => Math.round(Number(value || 0) * 100) / 100;

const uniqueOptions = (values: Array<string | undefined>) =>
  Array.from(new Set(values.map((value) => `${value || ''}`.trim()).filter(Boolean)));

const COLUMNS = [
  {
    title: 'Détenteur',
    width: 280,
    render: (_cell: any, row: DetenteurEntity) => (
      <div className='holder-cell'>
        <strong>{row.nom} {row.prenom || ''}</strong>
        <span>{row.code_referent || '--'} · {row.email || '--'}</span>
      </div>
    ),
  },
  {
    title: 'Agence',
    render: (_cell: any, row: any) => (
      <div className='position-cell'>
        <strong>{row.agence?.code || '--'}</strong>
        <span>{row.agence?.nom || '--'}</span>
      </div>
    ),
  },
  {
    title: 'Gestionnaire',
    render: (_cell: any, row: any) => `${row.createur?.nom || '--'} ${row.createur?.prenom || ''}`,
  },
  {
    title: 'Qté totale',
    render: (_cell: any, row: any) => (
      <span className='amount-cell'>{formatNum(row.qteTotal) || '--'}</span>
    ),
    align: 'right' as const,
  },
  {
    title: 'Montant total',
    render: (_cell: any, row: any) => (
      <span className='amount-cell'>{formatNum(row.montantTotal) || '--'} XAF</span>
    ),
    align: 'right' as const,
  },
  {
    title: 'P&L',
    dataIndex: 'pnl',
    render: (cell: any) => (
      <span className={`amount-cell ${cell >= 0 ? 'pnl-positive' : 'pnl-negative'}`}>
        {cell > 0 ? (
          <FaArrowUp />
        ) : (
          <FaArrowDown />
        )}{' '}
        {formatNum(cell) || '--'} XAF
      </span>
    ),
    align: 'right' as const,
  },
];

const SUB_COLUMNS: TableColumnsType<DetailTitreEntity> = [
  {
    title: 'Pays',
    render: (_cell: any, row: DetailTitreEntity) => (
      <div className='position-cell'>
        <strong>{row.titre?.pays || '--'}</strong>
        <span>{row.titre?.instrument || '--'}</span>
      </div>
    ),
  },
  {
    title: 'Code Titre',
    render: (_cell: any, row: DetailTitreEntity) => row.parentCode,
  },
  {
    title: 'Date acquisition',
    dataIndex: 'dateAcquisition',
    render: (cell: any) => `${moment(cell).format('DD/MM/YYYY')}`,
  },
  {
    title: 'Date attribution',
    dataIndex: 'created_at',
    render: (cell: any) => `${moment(cell).format('DD/MM/YYYY')}`,
  },
  {
    title: 'Date maturité',
    render: (_cell: any, row: DetailTitreEntity) =>
      `${moment(row.titre?.dateMaturite).format('DD/MM/YYYY')}`,
  },
  {
    title: 'Qte',
    dataIndex: 'qte',
    render: (cell: any) => <span className='amount-cell'>{formatNum(cell) || '--'}</span>,
    align: 'right' as const,
  },
  {
    title: 'Prix',
    dataIndex: 'prixNominale',
    render: (cell: any) => <span className='amount-cell'>{formatNum(cell * 1) || '--'}</span>,
    align: 'right' as const,
  },
  {
    title: 'Montant',
    dataIndex: 'montant',
    render: (cell: any) => <span className='amount-cell'>{formatNum(cell * 1) || '--'}</span>,
    align: 'right' as const,
  },
  {
    title: 'Décote',
    dataIndex: 'decote',
    render: (cell: any) => <span>{cell} %</span>,
  },
  {
    title: 'Coupon',
    dataIndex: 'tauxInteret',
    render: (cell: any) => <span>{cell} %</span>,
  },
  {
    title: 'Rendement',
    dataIndex: 'tauxRendement',
    render: (cell: any) => <span>{cell} %</span>,
  },
  {
    title: 'Fréquence',
    dataIndex: 'frequence',
  },
  {
    title: 'P&L',
    dataIndex: 'pnl',
    render: (cell: any) => (
      <span className={`amount-cell ${cell >= 0 ? 'pnl-positive' : 'pnl-negative'}`}>
        {cell > 0 ? (
          <FaArrowUp />
        ) : (
          <FaArrowDown />
        )}{' '}
        {formatNum(cell) || '--'} XAF
      </span>
    ),
    align: 'right' as const,
  },
];
